import {useCallback, useEffect, useMemo, useReducer, useRef} from 'react';
// import WebsocketAdaptor from '@cervello/socket-adaptor';
import debounce from 'lodash.debounce';
import useFilters from '@cervello/use-filters';
import {buildQuery, createDataReducer, initialState} from './utils';
import {
  Props,
  Aggregation,
  FilterValue,
  TimeWindow,
  Pagination,
  State,
  Sort,
  Actions,
} from './types';

// const listenerId = String(Math.round(Math.random() * 10000));
// let socketInstance: WebsocketAdaptor;

function useData<Data, Filters extends Record<string, FilterValue> = Record<string, FilterValue>>(
  props: Props<Data, Filters>,
) {
  const {
    fetchData,
    filters: propFilters,
    maintainPages = false,
    // initialPagination,
    cancelController,
    disableInitialLoad,
  } = props;
  const propsInitialPagination = props.initialPagination || {};
  const initialPagination = useMemo(() => propsInitialPagination, [
    propsInitialPagination.page,
    propsInitialPagination.limit,
  ]);
  const prevPagination = useRef<Pagination>(
    initialPagination
      ? {...initialState.pagination, ...initialPagination}
      : initialState.pagination,
  );

  const currentQueryRef = useRef<string>('');

  const cancellationToken = useRef<{
    signal: any;
    cancel: () => void;
  } | null>(null);
  const pendingRequest = useRef(false);

  const {filters, updateFilter, resetFilters, getFilterByKey} = useFilters<Filters>(
    props?.initialFilters || [],
  );

  const dataFetchReducer = useMemo(() => createDataReducer<Data, Filters>(), []);
  const reducerInitialState = useMemo(
    () => ({
      ...initialState,
      sort: props.initialSort,
      queryParams: props.initialQueryParams,
      aggregation: props.initialAggregation,
      timeWindow: props.initialTimeWindow,
      pagination: {
        ...initialState.pagination,
        ...props.initialPagination,
      },
      data: props.initialData || [],
      filters,
    }),
    [
      props.initialSort,
      props.initialQueryParams,
      props.initialTimeWindow,
      props.initialPagination,
      props.initialAggregation,
      props.initialData,
      filters,
    ],
  );

  const [
    {
      pagination,
      sort,
      data,
      aggregation,
      timeWindow,
      isLoading,
      queryParams,
      filters: appliedFilters,
      search,
    },
    dispatch,
  ] = useReducer(dataFetchReducer, reducerInitialState);

  const toggleSort = useCallback((key: string) => {
    dispatch({type: Actions.TOGGLE_SORT, payload: {key}});
  }, []);

  const setSort = useCallback(
    (sortOptions: Sort) => {
      dispatch({
        type: Actions.SET_SORT_OPTIONS,
        payload: {
          sortOption: sortOptions,
          initialPagination: initialPagination || {page: 1},
        },
      });
    },
    [initialPagination],
  );

  const resetSort = useCallback(() => {
    dispatch({
      type: Actions.SET_SORT_OPTIONS,
      payload: {
        sortOption: props.initialSort || {},
        initialPagination: initialPagination || {page: 1},
      },
    });
  }, [props.initialSort, initialPagination]);

  const setQueryParam = useCallback((queryParam: State<Data, Filters>['queryParams']) => {
    if (queryParam) {
      dispatch({type: Actions.SET_QUERY_PARAM, payload: queryParam});
    } else {
      dispatch({type: Actions.RESET_QUERY_PARAMS});
    }
  }, []);

  const setAggregation = useCallback((value: Aggregation) => {
    dispatch({type: Actions.SET_AGGREGATION, payload: value});
  }, []);

  const setTimewindow = useCallback((value: TimeWindow) => {
    dispatch({type: Actions.SET_TIMEWINDOW, payload: value});
  }, []);

  const setPagination = useCallback(
    (pagination: Partial<Pagination> | ((pagination: Pagination) => Partial<Pagination>)) => {
      dispatch({type: Actions.SET_PAGINATION, payload: pagination});
    },
    [],
  );

  const setDebouncedSearchValue = useCallback(
    debounce(searchValue => {
      if (searchValue) {
        setQueryParam({
          search: searchValue,
          newPagination: initialPagination || {page: 1},
        });
      } else {
        setQueryParam({
          search: undefined,
          newPagination: initialPagination || {page: 1},
        });
      }
    }, 500),
    [initialPagination],
  );

  const setSearch = useCallback(
    (searchValue?: string | null) => {
      // set search value real-time
      // set loading
      dispatch({type: Actions.SET_SEARCH_VALUE, payload: searchValue || undefined});
      // set queryParam in debounce
      setDebouncedSearchValue(searchValue);
    },
    [setDebouncedSearchValue],
  );

  const resetSearch = useCallback(() => {
    setSearch(null);
  }, [setSearch]);

  const query = useMemo(() => {
    return buildQuery<Data, Filters>({
      filters: appliedFilters,
      pagination,
      timeWindow,
      aggregation,
      sort,
      queryParams,
    });
  }, [
    pagination.page,
    sort,
    aggregation,
    timeWindow,
    pagination.limit,
    queryParams,
    appliedFilters,
  ]);

  const cancelRequest = () => {
    if (cancellationToken.current) {
      cancellationToken.current.cancel();
    }
  };

  const fetch = useCallback(() => {
    dispatch({type: Actions.FETCHING});
    if (pendingRequest.current) {
      cancelRequest();
    }
    if (cancelController) {
      cancellationToken.current = cancelController();
    }
    pendingRequest.current = true;
    currentQueryRef.current = query;

    fetchData({
      query,
      cancellationToken: cancellationToken.current?.signal,
    })
      ?.then(({data, count, limit, page, result}) => {
        pendingRequest.current = false;

        dispatch({
          type: Actions.SET_DATA,
          payload: {
            data: data || result || [],
            currentQueryRef,
            requestQuery: query,
            pagination: {
              count,
              prevPagination: prevPagination.current,
              maintainPages,
            },
          },
        });

        prevPagination.current = {page: Number(page), limit, count};
      })
      ?.catch(() => {
        dispatch({type: Actions.TOGGLE_FETCHING});
      });
  }, [query, fetchData, maintainPages]);

  useEffect(() => {
    dispatch({
      type: Actions.SET_FILTERS,
      payload: {
        filters: propFilters || filters,
        newPagination: initialPagination || {page: 1},
      },
    });
  }, [propFilters, filters, initialPagination]);

  const resetQuery = useCallback(() => {
    dispatch({
      type: Actions.RESET_QUERY,
      payload: {
        pagination: props.initialPagination,
        sort: props.initialSort,
        queryParams: props.initialQueryParams,
        search: props.initialSearch,
      },
    });
  }, [props.initialPagination, props.initialQueryParams, props.initialSearch, props.initialSort]);

  useEffect(() => {
    if (!disableInitialLoad) {
      fetch();
    }
  }, [fetch, disableInitialLoad]);

  const handleRealTime = useCallback(
    (data: Data[]) => {
      dispatch({type: Actions.UPDATE_DATA, payload: data});

      if (props.realTime?.callback) {
        props.realTime?.callback(data);
      }
    },
    [props.realTime],
  );

  useEffect(() => {
    if (!props?.realTime?.enable) {
      return;
    }
    // eslint-disable-next-line no-unused-vars
    // socketInstance = new WebsocketAdaptor(
    //   props.realTime.config.baseUrl,
    //   props.realTime.config.token,
    // );
    // init(props.realTime.config);
    // connectSocket(props.realTime.config.projectId);
    // listenOnEvent(props.realTime.topic, handleRealTime, listenerId);
    //
    // return () => removeEventListener(listenerId);
  }, [props.realTime, handleRealTime]);

  return {
    data,
    filters,
    sort,
    setSort,
    search, // queryParams?.search as string | undefined,
    setSearch,
    resetSearch,
    isLoading,
    pagination,
    fetch,
    refetch: fetch,
    getFilterByKey,
    updateFilter,
    resetFilters,
    resetSort,
    toggleSort,
    setAggregation,
    setTimewindow,
    setPagination,
    setQueryParam,
    resetQuery,
  };
}
export default useData;

export * from './types';
